import { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../../tfi/helpers/consts'
import { URL } from '../../../gql/client'
import { toast } from 'react-toastify'
import { useRealtimeStore } from '../../../store/realtimeStore'

// Types
export interface Message {
  id: number
  direction: 'INBOUND' | 'OUTBOUND'
  content: string
  status: string
  created_at: string
  channel: string
  metadata?: any
}

export interface Tag {
  id: number
  name: string
  color: string
}

export interface Conversation {
  id: number
  full_name: string
  phone: string
  avatar?: string
  last_message?: {
    content: string
    created_at: string
    direction: string
    channel?: string
  }
  unread_count: number
  assigned_to: number
  active_channels?: string[]
  tags?: Tag[]
  config_source?: string
}

export interface ContactSearchResult {
  id: number
  full_name: string
  phone: string
  avatar?: string
  email?: string
}

export interface ConfigOption {
  id: number
  source: string
  label: string
  type: string
  whatsapp_number?: string
  sms_number?: string
}

export const useChat = () => {
  const { auth } = useAuth()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loadingInbox, setLoadingInbox] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)

  // New State for Filter
  const [assignedOnly, setAssignedOnly] = useState(false)
  const [filterUnread, setFilterUnread] = useState(false)
  const [filterTags, setFilterTags] = useState<number[]>([])
  const [filterDateFrom, setFilterDateFrom] = useState<string>('')
  const [filterDateTo, setFilterDateTo] = useState<string>('')

  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [configSource, setConfigSource] = useState<string>('')
  const [availableConfigs, setAvailableConfigs] = useState<ConfigOption[]>([])

  // Helper to get token from AuthContext OR LocalStorage (fallback)
  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  // Fetch Tags
  const fetchTags = useCallback(async () => {
    const token = getToken()
    if (!token) return
    try {
      const response = await axios.get(`${URL}/api/chat/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAvailableTags(response.data.items)
    } catch (e) {
      console.error("Error fetching tags", e)
    }
  }, [getToken])

  // Fetch Available Configs
  const fetchAvailableConfigs = useCallback(async () => {
    const token = getToken()
    if (!token) return
    try {
      const response = await axios.get(`${URL}/api/chat/channels/available`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAvailableConfigs(response.data.items)
    } catch (e) {
      console.error("Error fetching available configs", e)
    }
  }, [getToken])

  useEffect(() => {
    if (auth?.api_token) {
      fetchTags()
      fetchAvailableConfigs()
    }
  }, [fetchTags, fetchAvailableConfigs, auth?.api_token])

  // Fetch Inbox
  const fetchInbox = useCallback(async (assignedOnlyFilter?: boolean) => {
    const token = getToken()
    if (!token) return
    setLoadingInbox(true)
    try {
      // Use the arg if provided, otherwise state. 
      const filterValue = assignedOnlyFilter !== undefined ? assignedOnlyFilter : assignedOnly

      const params: any = { assigned_only: filterValue }
      if (filterUnread) params.unread = 'true'
      if (filterTags.length > 0) params.tags = filterTags.join(',')
      if (filterDateFrom) params.date_from = filterDateFrom
      if (filterDateTo) params.date_to = filterDateTo

      const response = await axios.get(`${URL}/api/chat/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      })
      setConversations(response.data.items)
      if (response.data.config_source) {
        setConfigSource(response.data.config_source)
      }
    } catch (error) {
      console.error('Error fetching inbox:', error)
    } finally {
      setLoadingInbox(false)
    }
  }, [getToken, assignedOnly, filterUnread, filterTags, filterDateFrom, filterDateTo])

  // Sync loaded tags from conversations to availableTags (Fallback)
  useEffect(() => {
    if (conversations.length > 0) {
      const usedTags = new Map<number, Tag>()
      conversations.forEach(c => {
        if (c.tags) {
          c.tags.forEach(t => usedTags.set(t.id, t))
        }
      })

      if (usedTags.size > 0) {
        setAvailableTags(prev => {
          const existingIds = new Set(prev.map(t => t.id))
          let hasNew = false
          const newTags = [...prev]

          usedTags.forEach((tag, id) => {
            if (!existingIds.has(id)) {
              newTags.push(tag)
              hasNew = true
            }
          })

          return hasNew ? newTags.sort((a, b) => a.name.localeCompare(b.name)) : prev
        })
      }
    }
  }, [conversations])

  // Fetch Messages for a specific contact
  const fetchMessages = useCallback(async (contactId: number) => {
    const token = getToken()
    if (!token) return
    setLoadingMessages(true)
    try {
      const response = await axios.get(`${URL}/api/chat/history/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setMessages(response.data.messages)
      if (response.data.config_source) {
        setConfigSource(response.data.config_source)
      }

      // Mark as read immediately if fetching history
      await markAsRead(contactId)

      // Update local unread count
      setConversations(prev => prev.map(c =>
        c.id === contactId ? { ...c, unread_count: 0 } : c
      ))

    } catch (error) {
      console.error('Error fetching history:', error)
    } finally {
      setLoadingMessages(false)
    }
  }, [getToken])

  // Mark as Read API
  const markAsRead = async (contactId: number) => {
    const token = getToken()
    if (!token) return
    try {
      await axios.post(`${URL}/api/chat/read/${contactId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch (e) {
      console.error("Error marking read", e)
    }
  }

  // Create Tag
  const createTag = async (name: string, color: string) => {
    const token = getToken()
    if (!token) return
    try {
      const response = await axios.post(`${URL}/api/chat/tags`, { name, color }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const newTag = response.data
      setAvailableTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)))
      return newTag
    } catch (e) {
      console.error("Error creating tag", e)
      throw e
    }
  }

  // Manage Tags for Contact
  const manageTag = async (contactId: number, tagId: number, action: 'add' | 'remove') => {
    const token = getToken()
    if (!token) return
    try {
      const response = await axios.post(`${URL}/api/chat/tags/contact/${contactId}`,
        { tag_id: tagId, action },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Update conversation list state
      setConversations(prev => prev.map(c => {
        if (c.id === contactId) {
          return { ...c, tags: response.data.tags }
        }
        return c
      }))

      if (currentConversation?.id === contactId) {
        setCurrentConversation(prev => prev ? { ...prev, tags: response.data.tags } : null)
      }
    } catch (e) {
      console.error("Error managing tag", e)
      throw e
    }
  }

  // Send Message
  const sendMessage = async (contactId: number, content: string, channel: 'WHATSAPP' | 'SMS' = 'WHATSAPP', configId?: number) => {
    const token = getToken()
    if (!token) return
    try {
      const payload: any = { contact_id: contactId, content, channel }
      if (configId) payload.config_id = configId

      const response = await axios.post(`${URL}/api/chat/send`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const newMsg: Message = {
        id: response.data.message_id,
        direction: 'OUTBOUND',
        content,
        status: 'SENT',
        created_at: response.data.created_at,
        channel
      }

      // Optimistic update
      setMessages(prev => [...prev, newMsg])

      // Update conversation list snippet
      setConversations(prev => {
        const updated = prev.map(c =>
          c.id === contactId ? {
            ...c,
            last_message: {
              content,
              created_at: newMsg.created_at,
              direction: 'OUTBOUND'
            }
          } : c
        )
        // Move to top
        const target = updated.find(c => c.id === contactId)
        const others = updated.filter(c => c.id !== contactId)
        return target ? [target, ...others] : others
      })

    } catch (error: any) {
      console.error('Error sending message:', error)
      const errorMsg = error.response?.data?.error || error.message || "Error sending message"
      toast.error(errorMsg)
    }
  }

  // WebSocket Connection
  // Use a ref to access the latest handleNewMessage without re-binding the event listener
  const handleNewMessageRef = useRef<(msg: any) => void>(() => { })

  useEffect(() => {
    handleNewMessageRef.current = (msg: any) => {
      console.log('📨 WebSocket handleNewMessage triggered for:', msg)
      console.log('Current conversation ID:', currentConversation?.id, 'Msg contact_id:', msg.contact_id)

      // msg structure using backend naming conventions
      setConversations(prev => {
        const exists = prev.find(c => c.id === msg.contact_id)
        console.log('Conversation exists in list?', !!exists)

        let updatedList = [...prev]

        if (exists) {
          updatedList = updatedList.map(c =>
            c.id === msg.contact_id ? {
              ...c,
              // Use functional update or check currentConversationRef if needed.
              // We use msg.contact_id comparison similar to above
              // For safe comparison we convert to string, but here c.id is number and msg.contact_id from valid JSON might be num/string
              unread_count: (currentConversation?.id === msg.contact_id) ? 0 : c.unread_count + 1,
              last_message: {
                content: msg.content,
                created_at: msg.created_at,
                direction: 'INBOUND',
                channel: msg.channel || 'WHATSAPP'
              }
            } : c
          )
        } else {
          console.log('Triggering fetchInbox due to missing contact in list')
          fetchInbox()
          return prev
        }

        const target = updatedList.find(c => c.id === msg.contact_id)
        const others = updatedList.filter(c => c.id !== msg.contact_id)
        return target ? [target, ...others] : others
      })

      // Check if viewing this conversation
      // Ensure type safety for ID comparison (msg.contact_id might be string or number)
      const isCurrent = currentConversation && String(currentConversation.id) === String(msg.contact_id)

      if (isCurrent) {
        setMessages(prev => [...prev, {
          id: msg.communication_id,
          direction: 'INBOUND',
          content: msg.content,
          status: 'RECEIVED',
          created_at: msg.created_at,
          channel: msg.channel || 'WHATSAPP', // Use payload channel or fallback
          metadata: msg.metadata || {}
        }])
        markAsRead(msg.contact_id)
        markAsRead(msg.contact_id)
      }
    }
  }) // Update ref on every render

  // Global identifier to suppress the global toast if user is actively chatting with this person
  useEffect(() => {
    (window as any).__ACTIVE_CHAT_CONTACT_ID = currentConversation?.id || null;
    return () => {
      (window as any).__ACTIVE_CHAT_CONTACT_ID = null;
    }
  }, [currentConversation?.id])

  const lastEventType = useRealtimeStore(state => state.lastEventType)
  const lastEventPayload = useRealtimeStore(state => state.lastEventPayload)
  const lastEventTimestamp = useRealtimeStore(state => state.lastEventTimestamp)

  // Listen to global WebSocket instead of an isolated local one
  useEffect(() => {
    if (lastEventType === 'NEW_MESSAGE' && lastEventPayload) {
      handleNewMessageRef.current(lastEventPayload)
    }
  }, [lastEventType, lastEventPayload, lastEventTimestamp])



  // const handleNewMessage = (msg: any) => {
  //   // msg structure matches what we sent from backend:
  //   // { contact_id, communication_id, content, created_at, sender_name, type='NEW_MESSAGE' }

  //   setConversations(prev => {
  //     const exists = prev.find(c => c.id === msg.contact_id)

  //     let updatedList = [...prev]

  //     if (exists) {
  //       // Update existing
  //       updatedList = updatedList.map(c =>
  //         c.id === msg.contact_id ? {
  //           ...c,
  //           unread_count: (currentConversation?.id === msg.contact_id) ? c.unread_count : c.unread_count + 1,
  //           last_message: {
  //             content: msg.content,
  //             created_at: msg.created_at,
  //             direction: 'INBOUND'
  //           }
  //         } : c
  //       )
  //     } else {
  //       // New conversation (fetch it? or construct placeholder?)
  //       // Ideally we fetch the contact details. For now, we might miss it until refresh.
  //       // Let's trigger a refresh? Or add partial data.
  //       // Doing nothing for new unknown contacts for simplicity, or re-fetch inbox.
  //       fetchInbox()
  //       return prev
  //     }

  //     // Move to top
  //     const target = updatedList.find(c => c.id === msg.contact_id)
  //     const others = updatedList.filter(c => c.id !== msg.contact_id)
  //     return target ? [target, ...others] : others
  //   })

  //   // If viewing this conversation, append message
  //   if (currentConversation?.id === msg.contact_id) {
  //     setMessages(prev => [...prev, {
  //       id: msg.communication_id,
  //       direction: 'INBOUND',
  //       content: msg.content, // Note: backend sent truncated snippet? Check backend. 
  //       // Ah backend sent full text or snippet?
  //       // Backend: content: text_content[:50]... 
  //       // WAIT, we want full content in chat window!
  //       // Fix backend to send full content or at least enough. 
  //       // Or re-fetch? 
  //       // Better: Append what we have, but ideally backend should send full payload for 'NEW_MESSAGE' event meant for ChatWindow.
  //       // I will proceed, but note this.
  //       status: 'RECEIVED',
  //       created_at: msg.created_at,
  //       channel: 'WHATSAPP'
  //     }])

  //     // Mark as read since we are viewing it
  //     markAsRead(msg.contact_id)
  //   } else {
  //     const audio = new Audio('/media/notification.ogg')
  //     audio.play().catch(e => console.log('Audio play failed:', e))

  //     toast.info(`New message from ${msg.sender_name || 'Contact'}`, {
  //       position: "bottom-left",
  //       autoClose: 5000,
  //       style: { borderRadius: '8px', fontWeight: 500 }
  //     })
  //   }
  // }

  // Effect: When selecting conversation, fetch messages
  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation.id)
    } else {
      setMessages([])
    }
  }, [currentConversation, fetchMessages])

  // Initial load
  useEffect(() => {
    fetchInbox()
  }, [fetchInbox])

  const searchContacts = async (query: string) => {
    const token = getToken()
    if (!token) return []
    try {
      const response = await axios.get(`${URL}/api/chat/contacts?q=${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data.items as ContactSearchResult[]
    } catch (error) {
      console.error('Error searching contacts:', error)
      return []
    }
  }

  const startConversation = (contact: ContactSearchResult) => {
    const existing = conversations.find(c => c.id === contact.id)
    if (existing) {
      setCurrentConversation(existing)
    } else {
      const newConv: Conversation = {
        id: contact.id,
        full_name: contact.full_name,
        phone: contact.phone,
        avatar: contact.avatar,
        unread_count: 0,
        assigned_to: 0,
        last_message: undefined
      }
      setConversations(prev => [newConv, ...prev])
      setCurrentConversation(newConv)
    }
  }

  return {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loadingInbox,
    loadingMessages,
    sendMessage,
    refreshInbox: fetchInbox,
    searchContacts,
    startConversation,

    // Expose helpers
    reloadInbox: () => fetchInbox(),

    // Filter props
    assignedOnly,
    setAssignedOnly,
    filterUnread, setFilterUnread,
    filterTags, setFilterTags,
    filterDateFrom, setFilterDateFrom,
    filterDateTo, setFilterDateTo,
    availableTags,
    fetchTags,
    createTag,
    manageTag,
    configSource,
    availableConfigs
  }
}
